import { call, put, takeLatest, select } from 'redux-saga/effects';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { postTask } from '../../services';
import { Urls } from '../../config/urls';
import {
  refreshToken,
  refreshTokenError,
  tokenChecked,
  sendingLogin,
  loginSuccess,
  loginError,
  loginTwoFactorRequired,
  twoFactorVerifyError,
  logoutSuccess,
  logoutError,
  initCheckCompleted,
  AuthActionType,
} from './actions';
import { callbackUrlIsFromShopify, getLanguage, isRefreshTokenNecessary } from '../../utils';
import { PanelStore } from '../interfaces';
import { AuthSelectors } from './selectors';
import { segmentLogin } from '../../utils/analytics';
import { StatusSession } from '../../constants';

export function* checkToken(init?: boolean) {
  const state: PanelStore.RootState = yield select();
  
  const sessionIsInvalid = state.auth.sessionStatus === StatusSession.INVALID || 
                          state.auth.sessionStatus === StatusSession.LOGGED_OUT;
  
  const shouldRefresh = init 
    ? !sessionIsInvalid  
    : isRefreshTokenNecessary(state.auth);  
  
  if (shouldRefresh) {
    const { response, error } = yield call(postTask, {
      url: Urls.api.refreshLogin,
      body: {},
      token: state.auth.token,
    });

    if (response) {
      yield put(refreshToken(response));
      if (init) {
        yield put(tokenChecked());
      }
    } else {
      yield put(refreshTokenError(error));
      if (init) {
        yield put(tokenChecked());
      }
    }
  } else if (init) {
    yield put(tokenChecked());
  }
}

function* initLogin() {
  yield* checkToken(true);
}

export function* initLoginSaga() {
  yield takeLatest(AuthActionType.INIT_LOGIN, initLogin);
}

function* sendLogin(action: any) {
  yield put(sendingLogin());

  const { response, error } = yield call(postTask, {
    url: Urls.api.login,
    body: {
      username: action.email,
      password: action.password,
      merchantReference: action.merchantReference,
      merchantGaCookie: action.merchantGaCookie,
      merchantGaSessionIdCookie: action.merchantGaSessionIdCookie,
    },
  });

  if (response) {
    if (response.redirectUrl && callbackUrlIsFromShopify(response.redirectUrl)) {
      window.location.href = sanitizeUrl(response.redirectUrl);
      return;
    }

    if (response.twoFactorRequired) {
      yield put(loginTwoFactorRequired(response));
      return;
    }

    segmentLogin({
      language: getLanguage(),
    });
    yield put(loginSuccess(response));
  } else {
    yield put(loginError(error));
  }
}

export function* sendLoginSaga() {
  yield takeLatest(AuthActionType.SEND_LOGIN, sendLogin);
}

function* sendTwoFactorVerify(action: any) {
  yield put(sendingLogin());

  const { email, password, code, merchantReference, merchantGaCookie, merchantGaSessionIdCookie } = action;
  const { response, error } = yield call(postTask, {
    url: Urls.api.twoFactorVerifyLogin,
    body: {
      username: email,
      password,
      code,
      merchantReference,
      merchantGaCookie,
      merchantGaSessionIdCookie,
    },
  });

  if (response) {
    segmentLogin({
      language: getLanguage(),
    });
    yield put(loginSuccess(response));
  } else {
    yield put(twoFactorVerifyError(error));
  }
}

export function* sendTwoFactorVerifySaga() {
  yield takeLatest(AuthActionType.SEND_TWO_FACTOR_VERIFY, sendTwoFactorVerify);
}

function* sendLogout() {
  const token: string = yield select(AuthSelectors.getToken);
  const { response, error } = yield call(postTask, {
    url: Urls.api.logout,
    body: {},
    token,
  });
  if (response) {
    yield put(logoutSuccess(response));
  } else {
    yield put(logoutError(error));
  }
}

export function* sendLogoutSaga() {
  yield takeLatest(AuthActionType.SEND_LOGOUT, sendLogout);
}
